import React, { useEffect, useRef, useState } from 'react';
import {
  getQuestionTypeMetadata,
  questionHasStimulus,
  questionShowsAudioStimulus,
  deriveRenderLayoutFromStimulus,
  normalizeStimulus,
} from '@examflow/core';
import type { Question } from '@examflow/core';
import { QuestionCard } from './QuestionCard';
import { OptionItem } from './OptionItem';
import type { OptionState } from './OptionItem';

interface QuestionRendererProps {
  question: Question;
  questionNumber: number;
  selectedAnswer: string | null;
  showResult: boolean;
  showExplanation: boolean;
  mode: 'practice' | 'exam';
  onSelectAnswer: (optionId: string) => void;
  onShowExplanation: () => void;
  /**
   * 外层已按 passageSetId sticky 渲染阅读材料时跳过内部 PassageBlock，
   * 避免同文切题 remount 长文。
   */
  suppressPassage?: boolean;
}

export function QuestionRenderer({
  question,
  questionNumber,
  selectedAnswer,
  showResult,
  showExplanation,
  mode,
  onSelectAnswer,
  onShowExplanation,
  suppressPassage = false,
}: QuestionRendererProps) {
  const metadata = getQuestionTypeMetadata(question.type);
  const renderConfig = metadata?.renderConfig;
  const hasStimulus = normalizeStimulus(question.stimulus).length > 0;
  const layout = hasStimulus
    ? deriveRenderLayoutFromStimulus(question.stimulus)
    : renderConfig?.layout || 'default';
  const showFields = renderConfig?.showFields || ['content', 'options'];
  const options = question.options || [];
  const hasAudioStimulus = questionShowsAudioStimulus(question);
  const hasPassageStimulus = questionHasStimulus(question, 'passage');
  const levelLabel = question.level ? `难度 · ${question.level}` : undefined;
  const listeningPlayKey = `${question.id ?? 'q'}:${questionNumber}:${question.audioUrl ?? ''}`;

  const getOptionState = (optionId: string): OptionState => {
    if (!showResult) return selectedAnswer === optionId ? 'selected' : 'default';
    const correctAnswer = question.correctAnswer;
    const isCorrect = Array.isArray(correctAnswer)
      ? correctAnswer.includes(optionId)
      : optionId === correctAnswer;
    if (isCorrect) return 'correct';
    if (selectedAnswer === optionId) return 'wrong';
    return 'default';
  };

  const handleOptionsKeyDown = (e: React.KeyboardEvent) => {
    if (options.length === 0) return;
    const currentIdx = options.findIndex((o) => o.id === selectedAnswer);
    const idx = currentIdx < 0 ? 0 : currentIdx;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      onSelectAnswer(options[(idx + 1) % options.length].id);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onSelectAnswer(options[(idx - 1 + options.length) % options.length].id);
    }
  };

  return (
    <QuestionCard questionNumber={questionNumber} levelLabel={levelLabel}>
      {hasAudioStimulus && (
        <ListeningStimulusBlock
          audioUrl={question.audioUrl}
          audioStatus={question.audioStatus}
          playKey={listeningPlayKey}
        />
      )}

      {/* 听力题作答中不出 passage（文稿仅复盘/解析）；阅读题才显示「阅读材料」 */}
      {!suppressPassage && layout === 'passage-first' && question.passage && !hasAudioStimulus && (
        <ReadingPassageBlock
          passage={question.passage}
          stableKey={question.passageSetId ?? question.id}
        />
      )}

      {layout === 'audio-first' && question.audioUrl && !hasAudioStimulus && (
        <AudioBlock audioUrl={question.audioUrl} />
      )}

      {showFields.includes('content') && (
        <h2 className="mb-6 text-lg font-semibold leading-snug text-slate-900 sm:text-xl">
          {question.content}
        </h2>
      )}

      {!suppressPassage &&
        layout !== 'passage-first' &&
        question.passage &&
        !hasAudioStimulus &&
        (hasPassageStimulus || !hasStimulus) && (
          <ReadingPassageBlock
            passage={question.passage}
            stableKey={question.passageSetId ?? question.id}
          />
        )}

      {layout !== 'audio-first' && question.audioUrl && !hasAudioStimulus && (
        <AudioBlock audioUrl={question.audioUrl} />
      )}

      {question.imageUrl && (
        <div className="mb-6 flex max-h-96 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          <img
            src={question.imageUrl}
            alt="题目图片"
            className="h-auto max-h-96 w-auto max-w-full cursor-pointer object-contain transition-opacity hover:opacity-90"
            onClick={() => window.open(question.imageUrl!, '_blank')}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}

      {showFields.includes('options') && options.length > 0 && (
        <div
          className="space-y-3"
          role="radiogroup"
          aria-label="选项"
          onKeyDown={handleOptionsKeyDown}
          tabIndex={-1}
        >
          {options.map((option, idx) => (
            <OptionItem
              key={option.id}
              optionId={option.id}
              content={option.content}
              state={getOptionState(option.id)}
              onSelect={() => onSelectAnswer(option.id)}
              tabIndex={idx === 0 ? 0 : undefined}
            />
          ))}
        </div>
      )}

      {showFields.includes('options') && options.length === 0 && (
        <div className="py-6 text-center text-slate-500">该题目没有选项</div>
      )}

      {mode === 'practice' && showResult && (
        <ExplanationBlock
          question={question}
          showExplanation={showExplanation}
          onShowExplanation={onShowExplanation}
          hasAudioStimulus={hasAudioStimulus}
        />
      )}
    </QuestionCard>
  );
}

/**
 * Listening chrome: autoplay on enter; if the browser blocks play, show a one-click fallback.
 * Transcript (passage) is intentionally not shown here — only in ExplanationBlock / results.
 */
function ListeningStimulusBlock({
  audioUrl,
  audioStatus,
  playKey,
}: {
  audioUrl?: string;
  audioStatus?: string;
  playKey: string;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [needsGesture, setNeedsGesture] = useState(false);
  const [playError, setPlayError] = useState<string | null>(null);

  useEffect(() => {
    setNeedsGesture(false);
    setPlayError(null);
    const el = audioRef.current;
    if (!el || !audioUrl) return;

    el.pause();
    try {
      el.currentTime = 0;
    } catch {
      // ignore seek errors before metadata
    }

    let cancelled = false;
    void el
      .play()
      .then(() => {
        if (!cancelled) setNeedsGesture(false);
      })
      .catch(() => {
        if (!cancelled) setNeedsGesture(true);
      });

    return () => {
      cancelled = true;
      el.pause();
    };
  }, [audioUrl, playKey]);

  const handleManualPlay = () => {
    const el = audioRef.current;
    if (!el) return;
    setPlayError(null);
    void el
      .play()
      .then(() => setNeedsGesture(false))
      .catch(() => {
        setNeedsGesture(true);
        setPlayError('无法自动播放，请点击播放或检查浏览器媒体权限');
      });
  };

  return (
    <div className="mb-6 rounded-xl border-2 border-slate-300 bg-slate-100 p-5">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-700">
        听力材料
      </h3>
      {audioUrl ? (
        <div className="space-y-3">
          {needsGesture && (
            <button
              type="button"
              onClick={handleManualPlay}
              className="w-full rounded-lg border border-primary-300 bg-primary-100 px-4 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-200"
            >
              点击播放听力
            </button>
          )}
          {playError && <p className="text-sm text-amber-700">{playError}</p>}
          <audio
            ref={audioRef}
            controls
            src={audioUrl}
            className="w-full rounded-lg"
            preload="auto"
          >
            您的浏览器不支持音频播放
          </audio>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 py-4 text-slate-500">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm">
            {audioStatus === 'failed'
              ? '音频生成失败，请联系管理员重新生成'
              : '音频暂不可用，请联系管理员生成'}
          </span>
        </div>
      )}
    </div>
  );
}

/** 同文 sticky：父级用 passageSetId 作 key，避免切题重挂长文。 */
export function ReadingPassageBlock({
  passage,
  stableKey,
}: {
  passage: string;
  stableKey: string;
}) {
  return (
    <div
      data-passage-set={stableKey}
      className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-5"
    >
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
        阅读材料
      </h3>
      <div className="max-h-64 overflow-y-auto whitespace-pre-wrap leading-relaxed text-slate-700">
        {passage}
      </div>
    </div>
  );
}

function AudioBlock({ audioUrl }: { audioUrl: string }) {
  return (
    <div className="mb-6">
      <span className="mb-2 block text-sm font-medium text-slate-600">音频</span>
      <audio controls src={audioUrl} className="w-full rounded-lg" preload="metadata">
        您的浏览器不支持音频播放
      </audio>
    </div>
  );
}

function ExplanationBlock({
  question,
  showExplanation,
  onShowExplanation,
  hasAudioStimulus,
}: {
  question: Question;
  showExplanation: boolean;
  onShowExplanation: () => void;
  hasAudioStimulus: boolean;
}) {
  if (!question.explanation) {
    return <p className="mt-6 text-sm text-slate-500">本题暂无解析</p>;
  }

  return (
    <>
      {!showExplanation && (
        <button
          type="button"
          onClick={onShowExplanation}
          className="mt-6 rounded-lg border border-primary-300 bg-primary-100 px-4 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-200"
        >
          查看解析
        </button>
      )}
      {showExplanation && (
        <div className="mt-6 rounded-xl border border-primary-200 bg-primary-50 p-5">
          {hasAudioStimulus && question.passage && (
            <>
              <h4 className="mb-2 text-sm font-semibold text-primary-600">听力原文</h4>
              <p className="mb-4 whitespace-pre-wrap leading-relaxed text-slate-800">
                {question.passage}
              </p>
            </>
          )}
          <h4 className="mb-2 text-sm font-semibold text-primary-600">解析</h4>
          <p className="leading-relaxed text-slate-800">{question.explanation}</p>
        </div>
      )}
    </>
  );
}
